import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Alert, CircularProgress, Stack, InputAdornment,
  IconButton,
} from '@mui/material';
import { Chip } from '@mui/material'; 
import { VpnKey as KeyIcon, Mail, CheckCircle, Visibility,
  VisibilityOff, } from '@mui/icons-material';
import axios from 'axios';
import { KeyUri } from '../key';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
const [showConfirmPassword, setShowConfirmPassword] = useState(false);

const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong', 'Strong'];
  // Password strength
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const isPasswordStrong = passwordStrength === 4;

  // STEP 1: Send reset code
  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${KeyUri.BACKEND_URI}/vendor/password/reset-code`, { email });
      setStep('code');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  // STEP 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    const cleanOtp = verificationCode.replace(/[^0-9]/g, '');
    if (cleanOtp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${KeyUri.BACKEND_URI}/vendor/password/verify-reset-otp`, { 
        email, 
        otp: cleanOtp 
      });
      setStep('password');
      setNewPassword('');
setConfirmPassword('');
setShowNewPassword(false);
setShowConfirmPassword(false);

    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // STEP 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!isPasswordStrong) {
      setError('Password must be strong (8+ chars, uppercase, number, special char)');
      return;
    }
    try {
      setLoading(true);
      const cleanOtp = verificationCode.replace(/[^0-9]/g, '');
      await axios.post(`${KeyUri.BACKEND_URI}/vendor/password/reset`, { 
        email, 
        otp: cleanOtp, 
        newPassword 
      });
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);
const handleMouseDownPassword = (event) => event.preventDefault();


  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      p: 3,
      bgcolor: 'grey.50',
      marginTop: {lg: 10},
    }}>
      <Card sx={{ maxWidth: 450, width: '100%', boxShadow: 3 }}>
        <CardContent sx={{ p: 5 }}>
          
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <KeyIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your email to receive a verification code
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* STEP 1: Email */}
          {step === 'email' && (
            <Box component="form" onSubmit={handleSendCode} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Registered Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={loading || !email}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? 'Sending Code...' : 'Send Verification Code'}
              </Button>
            </Box>
          )}

          {/* STEP 2: Verification Code */}
          {step === 'code' && (
            <Box component="form" onSubmit={handleVerifyCode} sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Code sent to <strong>{email}</strong>
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: 1 } }}
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
            </Box>
          )}

          {/* STEP 3: New Password */}
          {step === 'password' && (
            <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 2 }}>
             <TextField
  fullWidth
  label="New Password"
  type={showNewPassword ? 'text' : 'password'}
  value={newPassword}
  onChange={(e) => setNewPassword(e.target.value)}
  required
  error={!isPasswordStrong && newPassword.length > 0}
  sx={{ mb: 2 }}
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleClickShowNewPassword}
          onMouseDown={handleMouseDownPassword}
          edge="end"
        >
          {showNewPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  }}
/>
<TextField
  fullWidth
  label="Confirm New Password"
  type={showConfirmPassword ? 'text' : 'password'}
  value={confirmPassword}
  onChange={(e) => setConfirmPassword(e.target.value)}
  error={newPassword !== confirmPassword && confirmPassword.length > 0}
  helperText={newPassword !== confirmPassword && confirmPassword.length > 0 ? 'Passwords do not match' : ''}
  required
  sx={{ mb: 3 }}
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleClickShowConfirmPassword}
          onMouseDown={handleMouseDownPassword}
          edge="end"
        >
          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  }}
/>

              
              {/* Password Strength */}
      {/* Password Strength Requirements */}
<Box sx={{ mb: 3 }}>
  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
    Password requirements:
  </Typography>
  <Stack spacing={1}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Chip size="small" color={newPassword.length >= 8 ? 'success' : 'default'} label="✓" />
      <Typography variant="body2">8+ characters</Typography>
    </Stack>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Chip size="small" color={/[A-Z]/.test(newPassword) ? 'success' : 'default'} label="✓" />
      <Typography variant="body2">Uppercase letter</Typography>
    </Stack>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Chip size="small" color={/[0-9]/.test(newPassword) ? 'success' : 'default'} label="✓" />
      <Typography variant="body2">Number</Typography>
    </Stack>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Chip size="small" color={/[^A-Za-z0-9]/.test(newPassword) ? 'success' : 'default'} label="✓" />
      <Typography variant="body2">Special character</Typography>
    </Stack>
  </Stack>
  <Chip 
    label={`Strength: ${strengthLabels[passwordStrength]}`} 
    color={isPasswordStrong ? 'success' : passwordStrength >= 3 ? 'warning' : 'error'}
    sx={{ mt: 2, fontWeight: 500 }}
  />
</Box>

              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={loading || newPassword !== confirmPassword || !isPasswordStrong}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Box>
          )}

          {/* SUCCESS */}
          {step === 'success' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="success.main">
                Password Reset Successful!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                You can now login with your new password.
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/vendor/login')}
                  fullWidth
                >
                  Go to Login
                </Button>
              </Stack>
            </Box>
          )}

          {/* Back to Login */}
          {step !== 'success' && (
            <Button 
              variant="text" 
              onClick={() => navigate('/vendor/login')} 
              sx={{ mt: 3, width: '100%', justifyContent: 'flex-start' }}
            >
              ← Back to Login
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
